// src/pages/About/ServicesPage.jsx (or wherever it lives)
import React from "react";
import { useParams } from "react-router-dom";
import "./ServicesPage.css";
import { useLocalContext } from "../../context/LocalContext";

const ServicesPage = () => {
  const { serviceId } = useParams();
  const { webinfo = {} } = useLocalContext();

  const brandName = webinfo.name || "Our team";

  // IDs aligned with the updated Services.jsx:
  // growth-blueprint, acquisition-sprints, site-funnels,
  // retention-crm, always-on-seo, embedded-pod
  const serviceData = [
    {
      id: "growth-blueprint",
      label: "Good starting point",
      title: "Full Check-Up of Your Online Presence",
      description:
        "See clearly what is helping your business online and what is holding it back.",
      details:
        "This is like a health check for your online presence. We look at your website, Google listing, ads and social pages. Then we show you, in very simple words, what is good, what is weak and what to fix first, so your online setup can start bringing in more real enquiries and sales.",
      outcomes: [
        "A clear list of what is working well and what is not.",
        "A simple “fix first, later, later” action plan for the next 1–3 months.",
        "Basic tracking cleaned up so you can see where leads are really coming from.",
      ],
      bestFor: [
        "Old or confusing websites.",
        "Businesses getting a few enquiries but want more.",
        "Owners who feel “something is wrong” but don’t know what.",
      ],
      features: [
        "Short call to understand your business and main services.",
        "Check of your website, forms, Google listing and basic tracking.",
        "Review of any running ads and social profiles.",
        "Simple notes on what to keep, what to improve and what to stop.",
        "Plain-language report (no jargon) that you can share with your team.",
        "Optional call to walk through the report and next steps together.",
      ],
      timeline: [
        "Day 1–2: Quick call and access to what we need (website, basic tools).",
        "Day 3–5: We review everything and prepare your report.",
        "Day 6–7: Share the report and walk you through the action plan.",
      ],
      testimonials: [
        {
          name: "Samir Patel",
          feedback:
            "We finally understood what was slowing us down online. The report was easy to follow and gave us a clear starting point.",
        },
        {
          name: "Laura Chen",
          feedback:
            "Instead of random advice, we got a simple list: fix these pages, stop these ads, add these basics. Very practical.",
        },
      ],
      faqs: [
        {
          question: "Do we need to prepare anything?",
          answer:
            "Not much. We’ll just ask for your website link, any ad accounts you’re using and how people usually contact you today.",
        },
        {
          question: "Is this only for big businesses?",
          answer:
            "No, this is perfect for small and growing businesses that want a clear picture before spending more on marketing.",
        },
        {
          question: "What do we get at the end?",
          answer:
            "You get a simple written report, a clear to-do list and an optional call where we explain everything in normal language.",
        },
      ],
    },

    {
      id: "acquisition-sprints",
      label: "More enquiries",
      title: "Google & Facebook / Instagram Ads for Leads",
      description:
        "Turn ad money into real calls, messages and form enquiries.",
      details:
        "We set up or clean up your Google and Facebook/Instagram ads so they stop wasting money and start bringing in real leads. We focus on simple, strong offers, the right audience and matching landing pages so more people who click actually contact you.",
      outcomes: [
        "Cleaner ad accounts that are easier to understand.",
        "More calls, messages and form fills from the same or lower ad spend.",
        "A clear idea of which ads and keywords are really working for you.",
      ],
      bestFor: [
        "Local service businesses (doctors, salons, repair etc.).",
        "Consultants, coaches and B2B services.",
        "Businesses already running ads but not happy with the results.",
      ],
      features: [
        "Check and clean-up of your existing Google and/or Facebook ad accounts.",
        "New or improved campaigns focused on real enquiries, not just clicks.",
        "Simple landing page suggestions or updates to match your ads.",
        "Basic tracking checks so we can see which ads bring leads.",
        "Weekly updates on what we changed and what results you’re getting.",
        "Suggestions for what to increase, pause or test next.",
      ],
      timeline: [
        "Week 1: Review your current setup, fix the worst issues and plan new campaigns.",
        "Week 2: Launch or relaunch ads with better settings and offers.",
        "Weeks 3–4: Tidy up, test and improve based on what the numbers show.",
      ],
      testimonials: [
        {
          name: "Nicole Alvarez",
          feedback:
            "Our calls and messages increased without increasing our budget. We finally understood which ads are worth keeping.",
        },
        {
          name: "Rohit Mehta",
          feedback:
            "Before this, our ad account was a mess. Now it’s simple and focused on the leads we actually want.",
        },
      ],
      faqs: [
        {
          question: "Do you also create the ad text and images?",
          answer:
            "Yes, we can write the ad text and create simple image ads, or work with your designer if you already have one.",
        },
        {
          question: "How much should we spend on ads?",
          answer:
            "We’ll suggest a starting budget based on your industry and location, and then adjust based on how well the ads perform.",
        },
        {
          question: "Can you work with our current agency or freelancer?",
          answer:
            "Yes, we can either take full control of the ads or work alongside your current partners with a clear split of responsibilities.",
        },
      ],
    },

    {
      id: "site-funnels",
      label: "Website & pages",
      title: "Simple, Lead-Focused Website & Pages",
      description:
        "Make it easy for visitors to understand you and contact you.",
      details:
        "We update or build key pages on your website so they look modern, load fast and are easy to use on mobile. The goal is simple: when someone lands on your site, they quickly understand what you do, trust you and know exactly how to take the next step.",
      outcomes: [
        "A website or key pages that match your current brand and services.",
        "More visitors turning into calls, messages and bookings.",
        "Pages that your team can edit later without breaking everything.",
      ],
      bestFor: [
        "Websites that look old or hard to use.",
        "Getting visitors but very few calls or enquiries.",
        "Launching a new service, product or city.",
      ],
      features: [
        "Short “story of your business” call to get your message right.",
        "New or refreshed homepage and key service/offer pages.",
        "Clear sections: who you are, what you do, proof, and strong calls to action.",
        "Mobile-friendly design so it works well on phones and tablets.",
        "Basic speed and usability checks so pages load fast and feel simple.",
        "Connection to your forms, WhatsApp, booking tools or payment links.",
      ],
      timeline: [
        "Week 1: Understand your business, plan the pages and create rough layouts.",
        "Week 2: Finalise text and designs, review together and adjust.",
        "Week 3–4: Build, test on different devices and go live.",
      ],
      testimonials: [
        {
          name: "James Whitaker",
          feedback:
            "The new site feels like how we talk in real life. People now call us saying they already understand what we do.",
        },
        {
          name: "Ananya Roy",
          feedback:
            "Our pages finally look modern and clean. We also know where to add new services without messing things up.",
        },
      ],
      faqs: [
        {
          question: "Can you work in our current platform (WordPress, Shopify, etc.)?",
          answer:
            "Yes, we usually build inside what you already use so you don’t have to move hosting or start from zero.",
        },
        {
          question: "Do you also handle images and content?",
          answer:
            "We can help with text, structure and basic images. If you need brand-new photos or deep branding work, we can suggest options.",
        },
        {
          question: "Is this a full website or just a few pages?",
          answer:
            "We can start with your most important pages and later expand into a full redesign if needed.",
        },
      ],
    },

    {
      id: "retention-crm",
      label: "Stay in touch",
      title: "Follow-Ups on Email & WhatsApp",
      description:
        "Remind people you exist so they come back again.",
      details:
        "Many people visit, ask once, or buy once — then forget you. We help you set up friendly, automated follow-ups on email and/or WhatsApp so past and warm leads remember your business and return when they need you again.",
      outcomes: [
        "More repeat business from people who already know and trust you.",
        "Less manual chasing because follow-ups run in the background.",
        "Clear view of who is new, who is warm and who might come back.",
      ],
      bestFor: [
        "Businesses with repeat customers.",
        "Trainers, teachers and course creators.",
        "Services that want more repeat work and referrals.",
      ],
      features: [
        "Review of how you currently collect and store email and phone details.",
        "Welcome and “thanks for contacting us” messages that send automatically.",
        "Simple reminder and check-in messages for past customers or leads.",
        "Basic list clean-up so you don’t send to dead or wrong addresses.",
        "Set up inside simple tools you’re comfortable using.",
        "Short guide so your team knows how to send and pause messages.",
      ],
      timeline: [
        "Week 1: Review your list, tools and customer journey.",
        "Week 2: Write and set up key follow-up messages.",
        "Week 3–4: Test, adjust and add extra messages if needed.",
      ],
      testimonials: [
        {
          name: "Jordan Blake",
          feedback:
            "Our old customers started coming back simply because we reminded them we were here. The messages felt natural, not spammy.",
        },
        {
          name: "Sneha Kulkarni",
          feedback:
            "We used to forget to follow up with leads. Now it happens automatically and we only step in when someone replies.",
        },
      ],
      faqs: [
        {
          question: "Will this annoy my customers?",
          answer:
            "No — we keep messages short, clear and spaced out. The goal is to be helpful, not to spam people.",
        },
        {
          question: "Do we need special software?",
          answer:
            "Usually, simple email or WhatsApp tools are enough. If you need something new, we’ll suggest easy-to-use options.",
        },
        {
          question: "Can my team handle it after setup?",
          answer:
            "Yes. We set it up and then show your team how to edit text, pause messages and add new ones later.",
        },
      ],
    },

    {
      id: "always-on-seo",
      label: "Be found",
      title: "Help People Find You on Google",
      description:
        "Show up better when people search for your type of business.",
      details:
        "We fix the important basics on your website so Google understands who you are and what you offer. Then we plan simple pages and articles around the real words your customers type into Google, so they find you instead of a competitor.",
      outcomes: [
        "More of the right people visiting your website from Google.",
        "A cleaner website structure that’s easier to navigate.",
        "Useful content that helps both search visitors and your sales team.",
      ],
      bestFor: [
        "Local shops and service providers.",
        "Specialised B2B businesses.",
        "Owners who want steady, long-term results instead of quick tricks.",
      ],
      features: [
        "Basic keyword research to see what people type when looking for your services.",
        "List of must-have pages and articles for your website.",
        "On-page fixes like titles, headings, internal links and meta descriptions.",
        "Suggestions to improve speed and overall website health.",
        "Simple content calendar with topics that support your main services.",
        "Monthly tracking of rankings, visits and enquiries from Google.",
      ],
      timeline: [
        "Month 1: Check your current site, fix important basics and plan content.",
        "Month 2–3: Create and improve key pages and helpful articles.",
        "Month 3+: Keep improving based on what people search and how they respond.",
      ],
      testimonials: [
        {
          name: "Ishaan Verma",
          feedback:
            "We now get steady enquiries from Google each month. The content is useful enough that we also send it to prospects directly.",
        },
        {
          name: "Claire Moreau",
          feedback:
            "Our blog used to be random. Now it’s a clear set of guides and pages that bring in the right visitors.",
        },
      ],
      faqs: [
        {
          question: "How long will it take to see results?",
          answer:
            "Some fixes help quite quickly, but strong, stable SEO usually takes a few months of consistent work.",
        },
        {
          question: "Do you write the content?",
          answer:
            "We can either write it for you or give your writers clear outlines and examples to follow.",
        },
        {
          question: "Will this affect our ads?",
          answer:
            "No. In fact, better pages usually help both your Google Ads and your free (organic) traffic work better together.",
        },
      ],
    },

    {
      id: "embedded-pod",
      label: "Ongoing help",
      title: "Part-Time Online Marketing Team",
      description:
        "Have a small expert team treating your business like their own.",
      details:
        "Instead of trying to manage many different freelancers and agencies, you get one small, steady team that looks after your main online channels. We plan, do the work and report back each month, so you always know what is happening and why.",
      outcomes: [
        "One team that understands your full online picture.",
        "Less stress trying to coordinate different people and tasks.",
        "Steady improvements across website, ads, content and follow-ups.",
      ],
      bestFor: [
        "Busy founders and owners.",
        "Small teams without a dedicated marketing person.",
        "Brands growing into new areas or countries.",
      ],
      features: [
        "Monthly planning call with clear goals and tasks.",
        "Ongoing work across website updates, ads, simple content and follow-ups.",
        "Short monthly report showing what we did and what changed.",
        "Regular suggestions on what to test or improve next.",
        "Simple communication channel (email or chat) for questions anytime.",
        "Flexible scope that can grow or shrink as your needs change.",
      ],
      timeline: [
        "Month 1: Understand your business, fix quick wins and set up basics.",
        "Month 2: Start a steady rhythm of updates and small experiments.",
        "Month 3+: Keep improving what works and removing what doesn’t.",
      ],
      testimonials: [
        {
          name: "Daniel Foster",
          feedback:
            "It feels like having an in-house marketing team, just without the headache of hiring full-time staff.",
        },
        {
          name: "Kavya Menon",
          feedback:
            "We finally have one place where all online work is planned and tracked. Decisions are much easier now.",
        },
      ],
      faqs: [
        {
          question: "Is there a minimum number of months?",
          answer:
            "We usually ask for at least 3 months so we can plan, implement and see real results together.",
        },
        {
          question: "Can you work with our existing team?",
          answer:
            "Yes. We can support your in-house staff and other partners and make sure everyone is moving in the same direction.",
        },
        {
          question: "How do we talk day to day?",
          answer:
            "We use simple tools like WhatsApp, email or Slack plus regular calls, depending on what works best for you.",
        },
      ],
    },
  ];

  const service = serviceData.find((s) => s.id === serviceId);

  if (!service) {
    return (
      <div className="service-details-container">
        <header className="service-header">
          <p className="service-eyebrow">Services</p>
          <h1>Service not found</h1>
          <p className="service-description">
            We couldn’t find that service. Please go back to the Services page and choose an option.
          </p>
        </header>
      </div>
    );
  }

  return (
    <div className="service-details-container">
      {/* Header */}
      <header className="service-header" aria-labelledby="service-title">
        <div className="service-badge-row">
          <span className="service-chip">{service.label}</span>
          <span className="service-eyebrow">Service detail</span>
        </div>
        <h1 id="service-title">{service.title}</h1>
        <p className="service-description">{service.description}</p>
      </header>

      {/* Main layout */}
      <div className="service-main">
        <div className="service-content" role="region" aria-label="Service content">
          {/* Overview */}
          <section className="section-card" aria-labelledby="overview-heading">
            <h2 id="overview-heading">What this looks like in practice</h2>
            <p>{service.details}</p>
          </section>

          {/* Outcomes + Best for */}
          <section className="section-card" aria-labelledby="outcomes-heading">
            <div className="section-split">
              <div>
                <h2 id="outcomes-heading">Outcomes you can expect</h2>
                <ul className="bullet-list">
                  {service.outcomes.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="subheading">Best suited for</h3>
                <div className="pill-cloud">
                  {service.bestFor.map((item, i) => (
                    <span key={i} className="pill">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="section-card" aria-labelledby="features-heading">
            <h2 id="features-heading">What we work on with you</h2>
            <div className="features-grid">
              {service.features.map((f, i) => (
                <div className="feature-item" key={i}>
                  <span className="tick" aria-hidden="true" />
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Timeline */}
          {service.timeline?.length > 0 && (
            <section className="section-card" aria-labelledby="timeline-heading">
              <h2 id="timeline-heading">Typical first phase snapshot</h2>
              <ol className="timeline-list">
                {service.timeline.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </section>
          )}

          {/* FAQs */}
          {service.faqs?.length > 0 && (
            <section className="section-card faqs" aria-labelledby="faqs-heading">
              <h2 id="faqs-heading">Common questions</h2>
              {service.faqs.map((qa, i) => (
                <details key={i}>
                  <summary>{qa.question}</summary>
                  <p>{qa.answer}</p>
                </details>
              ))}
            </section>
          )}
        </div>

        {/* Sidebar */}
        <aside className="sidebar" aria-label="Contact and meta">
          <div className="sidebar-card">
            <p className="sidebar-eyebrow">
              Ready to explore {service.label.toLowerCase()}?
            </p>
            <h3>Chat with {brandName}</h3>
            <p className="sidebar-copy">
              Tell us where you are today and what “good results” would look like in the next few months.
              We’ll help you see if this service is the right fit, or suggest a better starting point.
            </p>

            <div className="meta">
              <div>
                <strong>Call:</strong>{" "}
                <a href={`tel:${webinfo.phonecall || "+918000000000"}`}>
                  {webinfo.phone || "+91 80000 00000"}
                </a>
              </div>
              <div>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${webinfo.email || "hello@example.com"}`}>
                  {webinfo.email || "hello@example.com"}
                </a>
              </div>
              <div>
                <strong>Typical call length:</strong> 20–30 minutes
              </div>
            </div>

            <div className="pill-row">
              <span className="pill">No hard sell</span>
              <span className="pill">Clear next steps</span>
              <span className="pill">Simple language</span>
              <span className="pill">Shared numbers</span>
            </div>

            <button
              className="cta-button"
              onClick={() =>
                (window.location.href = `tel:${webinfo.phonecall || "+918000000000"}`)
              }
            >
              Call us: {webinfo.phone || "+91 80000 00000"}
            </button>
          </div>
        </aside>
      </div>

      {/* Testimonials */}
      {service.testimonials?.length ? (
        <section className="testimonials-section" aria-labelledby="testimonials-heading">
          <h2 id="testimonials-heading">What clients say about this kind of work</h2>
          <div className="testimonial-grid">
            {service.testimonials.map((t, i) => (
              <div className="testimonial-box" key={i}>
                <p>“{t.feedback}”</p>
                <span>— {t.name}</span>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default ServicesPage;
